import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { ProgressSpinner } from "primereact/progressspinner";
import "@google/model-viewer";

export default function ObjMtlViewer({ dwgUrl, glbUrl, objUrl, mtlUrl, metaData = [], width = "100%", height = "100%" }) {
    const mountRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const mapTexturesToMaterial = (mat, metaData) => {
        if (!Array.isArray(metaData)) return;

        const loader = new THREE.TextureLoader();

        // Map between "material property" and possible keywords
        const textureMapConfig = {
            map: ["diffuse", "albedo", "basecolor", "map_kd", "_0", "color"],
            roughnessMap: ["roughness", "map_ns", "gloss"],
            metalnessMap: ["metallic", "metalness", "map_refl"],
            normalMap: ["normal", "map_bump", "bump", "_2"],
            aoMap: ["ao", "ambientocclusion", "occlusion"],
            emissiveMap: ["emissive", "emission", "glow"],
        };

        // helper to find best-matching texture
        const findTex = (keywords) => metaData.find((url) => keywords.some((k) => url.toLowerCase().includes(k.toLowerCase())));

        // loop dynamically over config
        Object.entries(textureMapConfig).forEach(([prop, keywords]) => {
            const texUrl = findTex(keywords);
            if (texUrl) {
                mat[prop] = loader.load(texUrl);

                // Special cases
                if (prop === "roughnessMap") mat.roughness = 1;
                if (prop === "metalnessMap") mat.metalness = 1;
                if (prop === "emissiveMap") {
                    mat.emissive = new THREE.Color(0xffffff);
                    mat.emissiveIntensity = 1;
                }
            }
        });

        mat.side = THREE.DoubleSide;
        mat.needsUpdate = true;
    };
    useEffect(() => {
        const viewer = mountRef.current;

        if (!viewer) return;

        const handleLoad = () => setLoading(false);
        const handleError = () => {
            setLoading(false);
            setError(true);
        };

        viewer.addEventListener("load", handleLoad);
        viewer.addEventListener("error", handleError);

        return () => {
            viewer.removeEventListener("load", handleLoad);
            viewer.removeEventListener("error", handleError);
        };
    }, [glbUrl]);
    useEffect(() => {
        if (glbUrl) {
            setLoading(false);
            return;
        } // skip Three.js if GLB is available

        const currentMount = mountRef.current;
        const scene = new THREE.Scene();
        scene.background = new THREE.Color("#f0f0f0"); // light grey
        const renderer = new THREE.WebGLRenderer({ antialias: true });

        renderer.setSize(currentMount.clientWidth, currentMount.clientHeight);
        renderer.setClearColor(0xffffff, 1);
        currentMount.appendChild(renderer.domElement);

        // Camera
        const camera = new THREE.PerspectiveCamera(75, currentMount.clientWidth / currentMount.clientHeight, 0.1, 1000);

        camera.position.set(0, 2, 5);

        // Lighting
        scene.add(new THREE.AmbientLight(0xffffff, 0.6));
        const dirLight1 = new THREE.DirectionalLight(0xffffff, 1);
        dirLight1.position.set(5, 5, 5);
        scene.add(dirLight1);

        const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.5);
        dirLight2.position.set(-5, 5, -5);
        scene.add(dirLight2);

        const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.4);
        scene.add(hemiLight);

        // Controls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;

        // --- Load model ---
        const mtlLoader = new MTLLoader();

        mtlLoader.load(
            mtlUrl,
            (materials) => {
                materials.preload();
                const objLoader = new OBJLoader();
                objLoader.setMaterials(materials);

                objLoader.load(
                    objUrl,
                    (object) => {
                        object.traverse((child) => {
                            if (child.isMesh) {
                                // fallback: manually apply textures from metaData
                                mapTexturesToMaterial(child.material, metaData);

                                // Avoid black meshes
                                if (child.material.color && child.material.color.getHex() === 0x000000) {
                                    child.material.color.set(0xaaaaaa);
                                }
                            }
                        });

                        scene.add(object);

                        // Fit camera to object
                        const box = new THREE.Box3().setFromObject(object);
                        const center = box.getCenter(new THREE.Vector3());
                        const size = box.getSize(new THREE.Vector3()).length();

                        controls.target.copy(center);
                        controls.update();
                        controls.minDistance = size * 0.1; // ~20% closer
                        controls.maxDistance = size; // ~20% farther

                        // Place camera at maxDistance (farthest allowed point)
                        const direction = new THREE.Vector3(0, 0, 1); // default forward axis
                        camera.position.copy(center.clone().add(direction.multiplyScalar(controls.maxDistance)));
                        camera.lookAt(center);

                        // Update controls
                        controls.target.copy(center);
                        controls.update();
                        setLoading(false);
                    },
                    undefined,
                    (error) => {
                        console.error("Error loading OBJ:", error);
                        setError(true);
                        setLoading(false);
                    }
                );
            },
            undefined,
            (error) => {
                console.error("Error loading MTL:", error);
                setError(true);
                setLoading(false);
            }
        );

        // Animation loop
        const animate = () => {
            requestAnimationFrame(animate);
            controls.update();
            renderer.render(scene, camera);
        };
        animate();

        // Cleanup
        return () => {
            currentMount.removeChild(renderer.domElement);
            renderer.dispose();
        };
    }, [objUrl, mtlUrl, metaData]);

    return (
        <div
            style={{
                position: "relative",
                width: "100%",
                height: "400px",
                backgroundColor: "#f0f0f0",
            }}
        >
            {loading && (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(255, 255, 255, 0.9)",
                        zIndex: 1,
                        fontFamily: "Gerstner-ProgramRegular",
                        gap: "1rem",
                    }}
                >
                    <ProgressSpinner style={{ width: "3rem", height: "3rem" }} strokeWidth="8" />
                    <span style={{ fontSize: "1.2rem", color: "#555" }}>Loading 3D model...</span>
                </div>
            )}

            {error && (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(255, 255, 255, 0.9)",
                        zIndex: 1,
                        gap: "1rem",
                        fontFamily: "Gerstner-ProgramRegular",
                    }}
                >
                    <i className="pi pi-exclamation-triangle text-yellow-600" style={{ fontSize: "3rem" }} />
                    <span style={{ fontSize: "1.2rem", color: "#555" }}>Unable to load 3D Model</span>
                </div>
            )}

            {/* Use <model-viewer> if GLB available */}
            {glbUrl ? (
                <div ref={mountRef} style={{ display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "#f0f0f0" }}>
                    <model-viewer src={glbUrl} alt="3D Model" auto-rotate camera-controls style={{ width: "100%", height: "400px", border: "1px solid #BFAD5B", color: "red" }}></model-viewer>
                </div>
            ) : (
                <div ref={mountRef} style={{ width: "100%", height: "100%", backgroundColor: "#f0f0f0", border: "1px solid #BFAD5B" }} />
            )}
        </div>
    );
}
